import { useState, useEffect, useCallback } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ChevronLeft, Download, Loader2, BookOpen, Eye, FileType, ShieldCheck, Shield, History } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { getProfile, interpolate } from '../../lib/universityProfiles'
import { composeThesisDocument, buildPrintableHtml } from '../../utils/exportCompositionEngine'
import { validateFinalThesis } from '../../utils/thesisValidator'
import { downloadDocx } from '../../utils/docxExporter'
import { createSnapshot, loadSnapshot, loadLatestSnapshot, snapshotToChapters, snapshotToRegistries } from '../../utils/snapshotEngine'
import { validateAcademicCompleteness, validateFigureIntegrity } from '../../utils/academicValidator'
import ExportReadinessPanel from '../../components/ExportReadinessPanel'
import ThesisApprovalOverview from '../../components/ThesisApprovalOverview'
import { runAndPersistFigureCompletion, scanForFigureGaps } from '../../utils/export/autonomousFigureCompletionEngine'
import { useChapterStore } from '../../store/chapterStore'
import toast from 'react-hot-toast'

// ── CSS for exported thesis document ─────────────────────────────────────────

function buildThesisCSS(layout = {}) {
  const {
    fontFamily   = '"Times New Roman", Georgia, serif',
    fontSize     = '12pt',
    lineHeight   = '2',
    marginTop    = '1in',
    marginBottom = '1in',
    marginLeft   = '1.5in',
    marginRight  = '1in',
  } = layout

  return `
@page { size: A4; margin: ${marginTop} ${marginRight} ${marginBottom} ${marginLeft}; }
body { font-family: ${fontFamily}; font-size: ${fontSize}; line-height: ${lineHeight}; color: #000; }
.fm-page { page-break-after: always; min-height: 9in; position: relative; padding: 0.5in 0; }
.thesis-title { font-size: 16pt; font-weight: bold; }
h1.chapter-heading {
  font-family: ${fontFamily}; font-size: 14pt; font-weight: bold;
  text-align: center; text-transform: uppercase;
  margin: 0 0 2rem; page-break-before: always; page-break-after: avoid;
}
h2 { font-size: 12pt; font-weight: bold; margin: 1.5rem 0 0.75rem; }
h3 { font-size: 12pt; font-weight: bold; font-style: italic; margin: 1.25rem 0 0.5rem; }
h4 { font-size: 12pt; font-weight: bold; margin: 1rem 0 0.5rem; }
p  { margin: 0 0 1em; text-align: justify; text-indent: 0.5in; }
table { width: 100%; border-collapse: collapse; margin: 1.5rem 0; font-size: 11pt; }
th { background: #f0ece4; font-weight: bold; border: 1px solid #999; padding: 6px 10px; }
td { border: 1px solid #999; padding: 6px 10px; }
blockquote { border-left: 3px solid #555; padding-left: 1rem; margin: 1rem 0; font-style: italic; color: #333; }
ul, ol { padding-left: 1.5rem; margin: 0 0 1em; }
li { margin-bottom: 0.25em; }
.thesis-fig { margin: 1.5rem 0; page-break-inside: avoid; text-align: center; }
.thesis-fig pre.mermaid { text-align: left; background: #f8f6f2; border: 1px solid #ccc; padding: 0.75rem; border-radius: 4px; }
.lof-entry, .lot-entry { display: flex; align-items: baseline; gap: 0; margin-bottom: 0.5rem; }
.lof-entry .label { white-space: nowrap; color: #333; font-style: italic; min-width: 80px; }
.lof-entry .title { flex: 1; }
.lof-entry .dots  { flex: 1; border-bottom: 1px dotted #666; margin: 0 6px; min-width: 40px; }
.lof-entry .page  { white-space: nowrap; }
.toc-entry { margin-bottom: 0.4rem; }
.toc-chapter { font-weight: bold; }
.toc-section { margin-left: 1.5rem; }
`
}

// ── HTML thesis builder ────────────────────────────────────────────────────────

function buildVars(fm, thesis) {
  if (!fm) fm = {}
  return {
    thesis_title:           thesis?.title || '[Thesis Title]',
    student_name:           fm.student_name           || '[Student Name]',
    reg_number:             fm.reg_number             || '[Reg. No.]',
    degree:                 fm.degree                 || 'Doctor of Philosophy',
    specialization:         fm.specialization         || '[Specialization]',
    supervisor_name:        fm.supervisor_name        || '[Supervisor Name]',
    supervisor_designation: fm.supervisor_designation || '[Designation]',
    supervisor_institution: fm.supervisor_institution || '[Institution]',
    co_supervisor_name:     fm.co_supervisor_name     || '',
    examiner_name:          fm.examiner_name          || '[Examiner Name]',
    examiner_designation:   fm.examiner_designation   || '[Designation]',
    university_name:        fm.university_name || thesis?.university || '[University Name]',
    faculty_name:           fm.faculty_name           || '[Faculty/Department]',
    submission_month:       fm.submission_month       || '[Month]',
    submission_year:        fm.submission_year        || '[Year]',
    declaration_city:       fm.declaration_city       || '[City]',
    dedication_text:        fm.dedication_text        || '',
    acknowledgement_text:   fm.acknowledgement_text
      ? `<p>${fm.acknowledgement_text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`
      : '',
    abstract_text:          fm.abstract_text
      ? `<p>${fm.abstract_text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`
      : '',
    keywords_block:         (fm.keywords || []).length
      ? `<p style="margin-top:1rem"><strong>Keywords:</strong> ${(fm.keywords || []).join(', ')}</p>`
      : '',
  }
}

async function buildFullThesisHtml({ fm, thesis, chapters, profile }) {
  // Compose document — assigns authoritative figure/table numbers by document position
  const composedDoc = composeThesisDocument({ thesis, chapters, frontMatter: fm })

  // Build profile-based front matter HTML
  const vars     = buildVars(fm, thesis)
  const templates = profile?.templates || {}
  const renderSection = (key) => templates[key] ? interpolate(templates[key], vars) : ''
  const frontMatterOrder = profile?.frontMatterOrder || ['title', 'certificate', 'declaration', 'dedication', 'acknowledgements', 'abstract']
  const profileHtml = frontMatterOrder
    .filter(key => !['toc', 'list_of_figures', 'list_of_tables', 'abbreviations'].includes(key))
    .map(key => {
      if (key === 'dedication' && !fm?.dedication_text) return ''
      return renderSection(key) || ''
    })
    .join('\n')

  return await buildPrintableHtml({ composedDoc, profileHtml })
}

// ── Download helpers ──────────────────────────────────────────────────────────

function downloadHtml(html, filename) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url; a.download = filename
  document.body.appendChild(a); a.click(); a.remove()
  URL.revokeObjectURL(url)
}

// ── Status badge ──────────────────────────────────────────────────────────────

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ThesisExportPage() {
  const { id: thesisId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const snapshotParam = searchParams.get('snapshot')

  const [thesis,            setThesis]            = useState(null)
  const [chapters,          setChapters]          = useState([])
  const [fm,                setFm]                = useState(null)
  const [dbFigures,         setDbFigures]         = useState([])
  const [loading,           setLoading]           = useState(true)
  const [validating,        setValidating]        = useState(false)
  const [validation,        setValidation]        = useState(null)
  const [exporting,         setExporting]         = useState(false)
  const [exportingDocx,     setExportingDocx]     = useState(false)
  const [preview,           setPreview]           = useState(false)
  const [previewHtml,       setPreviewHtml]       = useState('')
  const [snapshot,          setSnapshot]          = useState(null)
  const [latestSnapshot,    setLatestSnapshot]    = useState(null)
  const [creatingSnapshot,  setCreatingSnapshot]  = useState(false)
  const [academicResult,         setAcademicResult]         = useState(null)
  const [figIntegrity,           setFigIntegrity]           = useState(null)
  const [userEmail,              setUserEmail]              = useState('')
  const [researchResults,        setResearchResults]        = useState(null)
  const [researchVariables,      setResearchVariables]      = useState([])
  const [figureGaps,             setFigureGaps]             = useState(null)
  const [figureCompletionRunning, setFigureCompletionRunning] = useState(false)

  // Derived from registry
  const [figures, setFigures] = useState(new Map())
  const [tables,  setTables]  = useState(new Map())

  const snapshotMode = !!snapshot

  // Subscribe to canonical chapter store — receives live status after any badge transition
  const storeChapters = useChapterStore(state => state.chapters)

  useEffect(() => {
    if (!thesisId) return
    const load = async () => {
      // Always load live thesis + FM for metadata
      const [{ data: t }, { data: f }, { data: figs }, { data: { user } }] = await Promise.all([
        supabase.from('theses').select('*').eq('id', thesisId).maybeSingle(),
        supabase.from('thesis_front_matter').select('*').eq('thesis_id', thesisId).maybeSingle(),
        supabase.from('thesis_figures').select('*').eq('thesis_id', thesisId).order('display_order'),
        supabase.auth.getUser(),
      ])
      setThesis(t)
      setFm(f)
      setDbFigures(figs || [])
      setUserEmail(user?.email || '')

      if (snapshotParam) {
        // Load frozen snapshot for export
        const snap = await loadSnapshot(snapshotParam).catch(() => null)
        if (snap) {
          setSnapshot(snap)
          const snapChapters = snapshotToChapters(snap)
          const { figures: fMap, tables: tMap } = snapshotToRegistries(snap)
          setChapters(snapChapters)
          setFigures(fMap)
          setTables(tMap)
        } else {
          toast.error('Snapshot not found — loading live data instead')
          await loadLive(t, f, figs)
        }
      } else {
        await loadLive(t, f, figs)
      }

      // Load latest snapshot for panel (always)
      const latest = await loadLatestSnapshot(thesisId).catch(() => null)
      setLatestSnapshot(latest)

      setLoading(false)
    }

    const loadLive = async (t, f, figs) => {
      const [{ data: chs }, { data: resRows }, { data: dsRows }] = await Promise.all([
        supabase.from('chapters').select('*').eq('thesis_id', thesisId).order('chapter_number'),
        supabase.from('thesis_research_results').select('analysis_type, result_json').eq('thesis_id', thesisId).order('created_at', { ascending: false }).limit(10),
        supabase.from('thesis_research_datasets').select('variables_config').eq('thesis_id', thesisId).order('created_at', { ascending: false }).limit(1),
      ])
      const sorted = (chs || []).sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))
      setChapters(sorted)
      useChapterStore.getState().hydrate(sorted)
      // Composition engine builds authoritative figure/table registries by document position
      const composed = composeThesisDocument({ thesis: t, chapters: sorted })
      setFigures(composed.figureRegistry)
      setTables(composed.tableRegistry)

      const resMap = resRows?.length
        ? Object.fromEntries(resRows.map(r => [r.analysis_type, r.result_json]))
        : null
      setResearchResults(resMap)

      const vars = dsRows?.[0]?.variables_config || []
      setResearchVariables(vars)

      // Compute figure gaps from live chapters + research results
      if (resMap && sorted.length > 0) {
        const gaps = scanForFigureGaps(sorted, resMap)
        setFigureGaps(gaps)
      }
    }

    load()
  }, [thesisId, snapshotParam])

  const runValidation = useCallback(async () => {
    setValidating(true)
    try {
      const [result, academic] = await Promise.all([
        validateFinalThesis(thesisId, { thesis, chapters, fm, figures: dbFigures }),
        Promise.resolve(validateAcademicCompleteness({ chapters, fm })),
      ])
      setValidation(result)
      setAcademicResult(academic)
      const figResult = validateFigureIntegrity(chapters, figures, tables)
      setFigIntegrity(figResult)
    } catch (err) {
      console.error('[ThesisExportPage] Validation error:', err)
      toast.error('Validation failed: ' + err.message)
    } finally {
      setValidating(false)
    }
  }, [thesisId, thesis, chapters, fm, dbFigures, figures, tables])

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true)
    try {
      const snap = await createSnapshot(thesisId, { label: '', userEmail })
      setLatestSnapshot(snap)
      toast.success(`Snapshot v${snap.version_number} created — score: ${snap.export_score}/100`)
    } catch (err) {
      toast.error('Snapshot failed: ' + err.message)
    } finally {
      setCreatingSnapshot(false)
    }
  }

  const profile = getProfile(fm?.university_profile || 'kennedy')

  const buildHtml = () => buildFullThesisHtml({ fm, thesis, chapters, profile })

  const handleExport = async () => {
    setExporting(true)
    try {
      const html = await buildHtml()
      const safe = (thesis?.title || 'thesis').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)
      downloadHtml(html, `${safe}-thesis.html`)
      toast.success('Thesis exported — open in browser and print to PDF')
    } catch (err) {
      toast.error('Export failed: ' + err.message)
    } finally {
      setExporting(false)
    }
  }

  const handlePreview = async () => {
    // Diagrams are rendered during the build now, so the preview must await it
    // like the download does; otherwise it renders "[object Promise]".
    setPreviewHtml(await buildHtml())
    setPreview(true)
  }

  const handleDocxExport = async () => {
    // If validation hasn't run, run it first (non-blocking for grace mode)
    let v = validation
    if (!v) {
      setValidating(true)
      try {
        v = await validateFinalThesis(thesisId, { thesis, chapters, fm, figures: dbFigures })
        setValidation(v)
      } finally {
        setValidating(false)
      }
    }

    // ── Export Grace Mode ─────────────────────────────────────────────────────
    // BLOCKED: unresolved placeholders, corrupted numbering, missing chapters
    // READY_WITH_ADVISORIES: stylistic notes, low confidence — proceed with notice
    // READY: clean export

    const hardBlocking = (v?.errors || []).filter(e =>
      /placeholder|corrupt|missing chapter|invalid figure anchor|broken reference|malformed/i.test(e.message || '')
    )

    if (hardBlocking.length > 0) {
      toast(
        `Export paused — ${hardBlocking.length} item(s) need attention before the final download: ${hardBlocking.map(e => e.message).join('; ')}.`,
        { icon: '📋', duration: 8000 }
      )
      return
    }

    // Advisory-only: proceed with a note
    const advisoryCount = (v?.warnings || []).length + ((v?.errors || []).length - hardBlocking.length)
    if (advisoryCount > 0) {
      toast(
        `Exporting with ${advisoryCount} advisory note${advisoryCount > 1 ? 's' : ''} — these are refinement suggestions and do not affect the document structure.`,
        { icon: '✅', duration: 4000 }
      )
    }

    setExportingDocx(true)
    try {
      const filename = await downloadDocx({ thesis, chapters, fm, dbFigures })
      toast.success(`Thesis downloaded: ${filename}`)
    } catch (err) {
      console.error('[DOCX export]', err)
      toast.error('Download could not be completed: ' + err.message)
    } finally {
      setExportingDocx(false)
    }
  }

  const handleAutoCompleteFigures = async () => {
    if (!researchResults) {
      toast.error('Run Research Lab first to compute analysis results before auto-generating figures.')
      return
    }
    setFigureCompletionRunning(true)
    try {
      const { generated, skipped, saved, errors, report } = await runAndPersistFigureCompletion(
        thesisId,
        chapters,
        { researchResults, variables: researchVariables, thesis }
      )

      if (errors.length > 0) {
        toast.error(`Figure completion: ${errors.length} save error(s). ${report}`)
      } else if (generated.length === 0) {
        toast.success('No figure gaps found — all chapters already have visual anchors.')
      } else {
        toast.success(`Auto-generated ${generated.length} figure(s) and saved ${saved} chapter(s).`)
      }

      // Refresh chapters and recompute gaps
      const { data: chs } = await supabase
        .from('chapters').select('*').eq('thesis_id', thesisId).order('chapter_number')
      const sorted = (chs || []).sort((a, b) => (a.chapter_number || 0) - (b.chapter_number || 0))
      setChapters(sorted)
      const recomposed = composeThesisDocument({ thesis, chapters: sorted })
      setFigures(recomposed.figureRegistry)
      setTables(recomposed.tableRegistry)
      setFigureGaps(scanForFigureGaps(sorted, researchResults))
    } catch (err) {
      console.error('[AutoCompleteFigures]', err)
      toast.error('Figure completion failed: ' + err.message)
    } finally {
      setFigureCompletionRunning(false)
    }
  }

  const completedChapters = chapters.filter(c =>
    ['DRAFT', 'REVIEWED', 'REVIEW', 'APPROVED', 'FINALIZED', 'LOCKED', 'EXPORT_LOCKED'].includes(c.status)
  )
  const approvedChapters  = chapters.filter(c => ['APPROVED', 'LOCKED', 'FINALIZED'].includes(c.status))
  const allApproved       = chapters.length > 0 && approvedChapters.length === chapters.length
  const totalWords = chapters.reduce((s, c) => s + (c.word_count || 0), 0)

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 4rem)' }}>
        <Loader2 style={{ width: 24, height: 24, color: '#2563eb', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  return (
    // The shared app shell's <main> is `p-6` and scrolls on its own overflow.
    // This page used to assume it owned the full viewport (100vh - 4rem), which
    // ignored both that padding and the real topbar height (h-14 = 3.5rem) — so
    // this box was taller than the space <main> actually gave it, and <main>'s
    // own scrollbar kicked in on top of this page's internal one. Negative
    // margins cancel the parent's padding so this fills exactly the space
    // <main> has, and its own overflow:hidden keeps <main> from ever scrolling.
    <div style={{ display: 'flex', height: 'calc(100vh - 3.5rem)', margin: '-1.5rem', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ── Sidebar ── */}
      <div style={{ width: 320, flexShrink: 0, background: '#FFFFFF', borderRight: '1px solid #DDE4EF', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: '1px solid #DDE4EF' }}>
          <Link to={`/app/thesis/${thesisId}/chapters`} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#506280', textDecoration: 'none', marginBottom: 12 }}>
            <ChevronLeft style={{ width: 12, height: 12 }} /> Chapters
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h1 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>Export Thesis</h1>
            <Link to={`/app/thesis/${thesisId}/versions`} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: '#64748b', textDecoration: 'none' }}>
              <History style={{ width: 11, height: 11 }} /> History
            </Link>
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>{thesis?.title}</p>
          {snapshotMode && (
            <div style={{ marginTop: 8, padding: '4px 8px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, fontSize: 11, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Shield style={{ width: 10, height: 10 }} />
              Snapshot v{snapshot.version_number} — frozen export
              <Link to={`/app/thesis/${thesisId}/export`} style={{ marginLeft: 'auto', color: '#2563eb', textDecoration: 'underline' }}>live</Link>
            </div>
          )}
        </div>

        {/* Snapshot panel */}
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF', background: latestSnapshot ? '#fafbff' : '#f8fafc' }}>
          {latestSnapshot ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Shield style={{ width: 11, height: 11, color: '#2563eb' }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1e40af' }}>v{latestSnapshot.version_number}</span>
                  <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, fontWeight: 600, background: latestSnapshot.export_status === 'READY' ? '#d1fae5' : '#e0f2fe', color: latestSnapshot.export_status === 'READY' ? '#065f46' : '#0c4a6e' }}>
                    {latestSnapshot.export_status}
                  </span>
                </div>
                <span style={{ fontSize: 10, color: latestSnapshot.export_score >= 80 ? '#059669' : latestSnapshot.export_score >= 50 ? '#d97706' : '#dc2626', fontWeight: 700 }}>
                  {latestSnapshot.export_score}/100
                </span>
              </div>
              <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                <button
                  onClick={handleCreateSnapshot}
                  disabled={creatingSnapshot}
                  style={{ flex: 1, fontSize: 10, padding: '3px 6px', borderRadius: 5, border: '1px solid #DDE4EF', background: '#fff', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, opacity: creatingSnapshot ? 0.6 : 1 }}
                >
                  {creatingSnapshot ? <Loader2 style={{ width: 9, height: 9, animation: 'spin 1s linear infinite' }} /> : <Shield style={{ width: 9, height: 9 }} />}
                  {creatingSnapshot ? 'Creating…' : 'New Snapshot'}
                </button>
                <Link
                  to={`/app/thesis/${thesisId}/export?snapshot=${latestSnapshot.id}`}
                  style={{ flex: 1, fontSize: 10, padding: '3px 6px', borderRadius: 5, border: '1px solid #93c5fd', background: '#eff6ff', color: '#1e40af', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, textDecoration: 'none' }}
                >
                  <Download style={{ width: 9, height: 9 }} /> Export v{latestSnapshot.version_number}
                </Link>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 6px' }}>No snapshot yet — create one to lock the thesis for export.</p>
              <button
                onClick={handleCreateSnapshot}
                disabled={creatingSnapshot || chapters.length === 0}
                style={{ width: '100%', fontSize: 11, padding: '4px 8px', borderRadius: 6, border: '1px solid #DDE4EF', background: '#f8fafc', color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, opacity: creatingSnapshot || chapters.length === 0 ? 0.6 : 1 }}
              >
                {creatingSnapshot ? <Loader2 style={{ width: 10, height: 10, animation: 'spin 1s linear infinite' }} /> : <Shield style={{ width: 10, height: 10 }} />}
                {creatingSnapshot ? 'Creating snapshot…' : 'Create Snapshot'}
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #DDE4EF', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <StatCard label="Chapters"    value={`${completedChapters.length}/${chapters.length}`} ok={completedChapters.length > 0} />
          <StatCard label="Total words" value={totalWords.toLocaleString()}  ok={totalWords > 0} />
          <StatCard label="Figures"     value={figures.size} ok />
          <StatCard label="Tables"      value={tables.size}  ok />
        </div>

        {/* Chapter approval overview */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <ThesisApprovalOverview thesisId={thesisId} chapters={chapters} />
        </div>

        {/* Actions */}
        <div style={{ padding: '0.75rem', borderTop: '1px solid #DDE4EF', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button
            onClick={runValidation}
            disabled={validating || loading}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.5rem', borderRadius: 8, border: '1px solid #DDE4EF', background: '#f8fafc', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: validating ? 0.7 : 1 }}
          >
            {validating ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <ShieldCheck style={{ width: 14, height: 14 }} />}
            {validating ? 'Validating…' : 'Validate Thesis'}
          </button>
          <button
            onClick={() => navigate(`/app/thesis/${thesisId}/preview`)}
            disabled={chapters.length === 0}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.5rem', borderRadius: 8, border: '1px solid #DDE4EF', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: chapters.length === 0 ? 0.6 : 1 }}
          >
            <Eye style={{ width: 14, height: 14 }} /> Preview (A4)
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || chapters.length === 0}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.5rem', borderRadius: 8, border: '1px solid #DDE4EF', background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: exporting || chapters.length === 0 ? 0.6 : 1 }}
          >
            {exporting ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Download style={{ width: 14, height: 14 }} />}
            {exporting ? 'Exporting…' : 'Export HTML'}
          </button>
          {/* Auto-complete figures (Phase 7) */}
          {researchResults && (
            <button
              onClick={handleAutoCompleteFigures}
              disabled={figureCompletionRunning || chapters.length === 0}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.5rem', borderRadius: 8, border: '1px solid #bbf7d0', background: figureGaps?.needsFigures ? '#f0fdf4' : '#f8fafc', color: figureGaps?.needsFigures ? '#065f46' : '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: figureCompletionRunning || chapters.length === 0 ? 0.6 : 1, position: 'relative' }}
            >
              {figureCompletionRunning ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <BookOpen style={{ width: 14, height: 14 }} />}
              {figureCompletionRunning ? 'Generating Figures…' : 'Auto-Complete Figures'}
              {figureGaps?.needsFigures && !figureCompletionRunning && (
                <span style={{ position: 'absolute', top: -4, right: -4, width: 16, height: 16, borderRadius: '50%', background: '#059669', color: '#fff', fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {figureGaps.count}
                </span>
              )}
            </button>
          )}
          <button
            onClick={handleDocxExport}
            disabled={exportingDocx || validating || chapters.length === 0}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '0.5rem', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: exportingDocx || chapters.length === 0 ? 0.6 : 1 }}
          >
            {exportingDocx ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <FileType style={{ width: 14, height: 14 }} />}
            {exportingDocx ? 'Generating DOCX…' : 'Download DOCX'}
          </button>
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {preview ? (
          <>
            <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DDE4EF', padding: '0.5rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Thesis Preview</span>
              <button onClick={() => setPreview(false)} style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>← Back</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', background: '#1a1410', display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <div
                style={{ maxWidth: 860, width: '100%', background: '#f5f0e8', padding: '1.5in 1in', boxShadow: '0 0 60px rgba(0,0,0,0.6)', minHeight: '11in' }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </div>
          </>
        ) : (
          <div style={{ flex: 1, overflow: 'auto', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: 700, margin: '0 auto', width: '100%' }}>

            {/* Intro card */}
            <div style={{ textAlign: 'center', padding: '1.5rem', background: '#FFFFFF', border: '1px solid #DDE4EF', borderRadius: 12 }}>
              <BookOpen style={{ width: 36, height: 36, color: '#2563eb', marginBottom: '0.75rem' }} />
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>Professional Thesis Export</h2>
              <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                Export your complete thesis as a real <strong>DOCX</strong> (Microsoft Word) or <strong>HTML</strong> file for PDF printing.
                Run validation first to catch placeholders, broken sentences, and numbering issues.
              </p>
            </div>

            {/* Snapshot mode banner */}
            {snapshotMode && (
              <div style={{ background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 12, padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: 12 }}>
                <Shield style={{ width: 20, height: 20, color: '#2563eb', flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#1e40af', margin: 0 }}>Exporting from Snapshot v{snapshot.version_number}</p>
                  <p style={{ fontSize: 12, color: '#3b82f6', margin: '2px 0 0' }}>
                    This is a frozen export — content, figures, and numbering are locked as of {new Date(snapshot.created_at).toLocaleString()}.
                    {snapshot.label && ` Label: "${snapshot.label}".`}
                  </p>
                </div>
              </div>
            )}

            {/* Validation panel */}
            <ExportReadinessPanel
              validation={validation}
              loading={validating}
              onRecheck={runValidation}
            />

            {/* Academic completeness panel */}
            {academicResult && (
              <div style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', borderRadius: 12, padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 }}>Academic Completeness</h3>
                  <span style={{ fontSize: 20, fontWeight: 800, color: academicResult.completenessScore >= 75 ? '#059669' : academicResult.completenessScore >= 50 ? '#d97706' : '#dc2626' }}>
                    {academicResult.completenessScore}<span style={{ fontSize: 12, fontWeight: 400, color: '#94a3b8' }}>/100</span>
                  </span>
                </div>
                {academicResult.missingSections.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', margin: '0 0 4px' }}>Missing required sections:</p>
                    {academicResult.missingSections.map(s => (
                      <div key={s} style={{ fontSize: 12, color: '#dc2626', padding: '2px 0' }}>• {s}</div>
                    ))}
                  </div>
                )}
                {academicResult.weakSections.length > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: '#d97706', margin: '0 0 4px' }}>Weak sections:</p>
                    {academicResult.weakSections.map(w => (
                      <div key={w.chapter} style={{ fontSize: 11, color: '#92400e', padding: '2px 0', lineHeight: 1.5 }}>
                        • {w.chapter} — score {w.componentScore}/100
                        {w.shortfall > 0 && `, ${w.shortfall.toLocaleString()} words short`}
                        {w.missingComponents.length > 0 && ` (missing: ${w.missingComponents.join(', ')})`}
                      </div>
                    ))}
                  </div>
                )}
                {academicResult.structuralWarnings.map(w => (
                  <div key={w} style={{ fontSize: 11, color: '#7c3aed', padding: '2px 0' }}>⚠ {w}</div>
                ))}
                {academicResult.citationConfidence && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                    <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
                      Citation confidence: <strong style={{ color: academicResult.citationConfidence.confidenceScore >= 70 ? '#059669' : '#d97706' }}>{academicResult.citationConfidence.confidenceScore}/100</strong>
                      {' '}· {academicResult.citationConfidence.totalCitations} citation(s) detected
                      {academicResult.citationConfidence.suspiciousCitations.length > 0 && (
                        <span style={{ color: '#dc2626' }}> · {academicResult.citationConfidence.suspiciousCitations.length} suspicious</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Figure integrity panel */}
            {figIntegrity && figIntegrity.issues.length > 0 && (
              <div style={{ background: '#FFFFFF', border: '1px solid #fca5a5', borderRadius: 12, padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0 }}>Figure Integrity</h3>
                  <span style={{ fontSize: 13, fontWeight: 700, color: figIntegrity.figureIntegrityScore >= 80 ? '#059669' : '#dc2626' }}>
                    {figIntegrity.figureIntegrityScore}/100
                  </span>
                </div>
                {figIntegrity.issues.map((iss, i) => (
                  <div key={i} style={{ fontSize: 11, color: '#b91c1c', padding: '2px 0' }}>• [{iss.type}] {iss.message}</div>
                ))}
              </div>
            )}

            {/* Workflow approval status */}
            <div style={{ background: allApproved ? 'rgba(22,163,74,0.06)' : 'rgba(220,38,38,0.04)', border: `1px solid ${allApproved ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.2)'}`, borderRadius: 12, padding: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', margin: 0 }}>
                  Chapter Approval
                </p>
                <span style={{ fontSize: 12, fontWeight: 700, color: allApproved ? '#16a34a' : '#dc2626' }}>
                  {approvedChapters.length} / {chapters.length} approved
                </span>
              </div>
              {chapters.map(ch => (
                <div key={ch.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: ['APPROVED', 'LOCKED', 'FINALIZED'].includes(ch.status) ? '#10b981' : '#dc2626' }}>
                    {['APPROVED', 'LOCKED', 'FINALIZED'].includes(ch.status) ? '✓' : '○'}
                  </span>
                  <span style={{ fontSize: 12, flex: 1, color: '#334155' }}>Ch. {ch.chapter_number}: {ch.title}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: ['APPROVED', 'LOCKED', 'FINALIZED'].includes(ch.status) ? '#16a34a' : ch.status === 'UNDER_REVIEW' ? '#d97706' : '#94a3b8' }}>
                    {ch.status || 'DRAFT'}
                  </span>
                </div>
              ))}
              {!allApproved && (
                <p style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
                  Approval is optional and tracked for your reference only — export works regardless of chapter status.
                </p>
              )}
            </div>

            {/* Pre-export checklist */}
            {!validation && (
              <div style={{ background: '#FFFFFF', border: '1px solid #DDE4EF', borderRadius: 12, padding: '1.25rem' }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
                  Pre-export checklist
                </p>
                <CheckItem ok={!!fm?.student_name}         label="Student details filled"    link={`/app/thesis/${thesisId}/front-matter`} />
                <CheckItem ok={!!fm?.supervisor_name}      label="Supervisor name filled"     link={`/app/thesis/${thesisId}/front-matter`} />
                <CheckItem ok={!!fm?.submission_year}      label="Submission year filled"     link={`/app/thesis/${thesisId}/front-matter`} />
                <CheckItem ok={completedChapters.length > 0} label={`${completedChapters.length} of ${chapters.length} chapters with content`} />
                <CheckItem ok={!!fm?.abstract_text}        label="Abstract written"           link={`/app/thesis/${thesisId}/front-matter`} />
                <CheckItem ok={figures.size > 0}           label={`${figures.size} figure(s) registered`} />
                <CheckItem ok={allApproved}                label={`All chapters approved (${approvedChapters.length}/${chapters.length})`} />
              </div>
            )}

          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, ok }) {
  return (
    <div style={{ background: ok ? 'rgba(37,99,235,0.06)' : '#f8fafc', borderRadius: 8, padding: '0.6rem 0.75rem', border: `1px solid ${ok ? 'rgba(37,99,235,0.15)' : '#DDE4EF'}` }}>
      <p style={{ fontSize: 18, fontWeight: 700, color: ok ? '#2563eb' : '#94a3b8', margin: 0 }}>{value}</p>
      <p style={{ fontSize: 11, color: '#64748b', margin: '2px 0 0' }}>{label}</p>
    </div>
  )
}

function CheckItem({ ok, label, link }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.5rem' }}>
      <span style={{ fontSize: 14, color: ok ? '#10b981' : '#94a3b8' }}>{ok ? '✓' : '○'}</span>
      <span style={{ fontSize: 13, color: ok ? '#334155' : '#94a3b8' }}>
        {link && !ok ? <Link to={link} style={{ color: '#2563eb' }}>{label}</Link> : label}
      </span>
    </div>
  )
}
